import axiosInstance from '../fetchConfig/axiosConfig';
import { usePersistedDataStore } from '../components/store';
import { isCaseSettled } from '../components/CaseReview/SummaryCaseTable/utils/helpers';
import logger from './logger';

const REPORT_TYPES_SKIP_TASK_CREATION = ['marketing', 'evidence_comparison'];

const getCaseByParcelId = (negotiationObj, parcelId) =>
  negotiationObj?.cases?.find((c) => String(c.parcel_id) === String(parcelId));

const filterMissingTasksExcludingSettled = (missingTasks, negotiationObj) => {
  const filtered = {};
  const skippedSettledParcelIds = [];

  for (const [parcelId, reviewTypes] of Object.entries(missingTasks || {})) {
    const caseItem = getCaseByParcelId(negotiationObj, parcelId);
    if (caseItem && isCaseSettled(caseItem)) {
      skippedSettledParcelIds.push(parcelId);
      continue;
    }
    filtered[parcelId] = reviewTypes;
  }

  return { filtered, skippedSettledParcelIds };
};

const filterUidsExcludingSettled = (uids, negotiationObj) => {
  if (!Array.isArray(uids)) return [];
  return uids.filter((uid) => {
    const caseItem = getCaseByParcelId(negotiationObj, uid);
    return !caseItem || !isCaseSettled(caseItem);
  });
};

export const createTasks = async (missingTasks) => {
  const negotiationObj = usePersistedDataStore.getState().negotiationObj;
  const taxYear = negotiationObj?.TaxYear;
  const villageRun = negotiationObj?.villageRun || 0;

  const { filtered: tasksToCreate, skippedSettledParcelIds } =
    filterMissingTasksExcludingSettled(missingTasks, negotiationObj);
  const missingCount = Object.keys(tasksToCreate).length;

  if (!taxYear || missingCount === 0) {
    return;
  }

  const payload = {
    missing_tasks: tasksToCreate,
    year: taxYear,
    villageRun,
  };

  logger.log('[TASK_CREATION] createTasks', {
    missingCount,
    skippedSettledCount: skippedSettledParcelIds.length,
  });
  const response = await axiosInstance.post('/create_tasks', payload);
  return response.data;
};

export const checkTaskExistence = async () => {
  const negotiationObj = usePersistedDataStore.getState().negotiationObj;
  const reportType = negotiationObj?.reportType || 'standard';

  if (REPORT_TYPES_SKIP_TASK_CREATION.includes(reportType)) {
    return;
  }

  const uidsForCheck = filterUidsExcludingSettled(negotiationObj?.uids, negotiationObj);
  const taskParams = {
    muniCode: negotiationObj?.MuniCode,
    taxYear: negotiationObj?.TaxYear,
    uids: uidsForCheck,
    villageRun: negotiationObj?.villageRun || 0,
  };

  // DO NOT CREATE TASKS SINGLE PARCEL RUNS -- we should guard for the other runtypes too
  if (
    !negotiationObj ||
    !['all_clients', 'court_date'].includes(negotiationObj.runType) ||
    !taskParams.muniCode ||
    (Array.isArray(negotiationObj.uids) && negotiationObj.uids.length === 1)
  ) {
    return;
  }

  if (Array.isArray(negotiationObj.uids) && negotiationObj.uids.length > 0 && uidsForCheck.length === 0) {
    logger.log('[TASK_CREATION] skipped: all parcels settled');
    return 0;
  }

  try {
    const response = await axiosInstance.post('/check_task_existence', { taskParams });
    const missingTasks = response.data.missing_tasks || {};
    const { filtered: missingTasksToCreate, skippedSettledParcelIds } =
      filterMissingTasksExcludingSettled(missingTasks, negotiationObj);
    const missingCount = Object.keys(missingTasksToCreate).length;

    if (missingCount === 0) {
      return 0;
    }

    logger.log('[TASK_CREATION] creating tasks', {
      missingCount,
      skippedSettledCount: skippedSettledParcelIds.length,
    });
    await createTasks(missingTasksToCreate);
    return missingCount;
  } catch (error) {
    logger.error('[TASK_CREATION] checkTaskExistence failed', error);
    throw error;
  }
};
